import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { adminLogin, getAdminToken } from "@/lib/api";
import { useEffect } from "react";

export default function AdminLogin() {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (getAdminToken()) navigate("/admin/dashboard", { replace: true });
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      await adminLogin(password);
      toast.success("Welcome back, Coach.");
      navigate("/admin/dashboard");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Invalid password";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      data-testid="admin-login-page"
      className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-6 relative overflow-hidden"
    >
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none">
        <div className="h-full w-full bg-[linear-gradient(to_right,white_1px,transparent_1px),linear-gradient(to_bottom,white_1px,transparent_1px)] bg-[size:80px_80px]" />
      </div>

      <div className="absolute top-6 left-6">
        <button
          onClick={() => navigate("/")}
          data-testid="admin-back-home"
          className="flex items-center gap-2"
        >
          <span className="inline-block w-2 h-2 rounded-full bg-[#eab308] bham-dot" />
          <span className="font-display text-xl tracking-tight">
            BHAM<span className="text-[#eab308]">.</span>BASEBALL
          </span>
        </button>
      </div>

      <form
        onSubmit={submit}
        className="relative w-full max-w-md bg-[#141414] border border-[#2a2a2a] p-8 md:p-10"
        data-testid="admin-login-form"
      >
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[#eab308]/10 border border-[#eab308]/30 mb-6">
          <Lock className="text-[#eab308]" size={20} />
        </div>
        <div className="font-condensed uppercase tracking-[0.25em] text-xs text-[#eab308] mb-2">
          Coach Access
        </div>
        <h1 className="font-display text-3xl md:text-4xl mb-2">Admin Dashboard</h1>
        <p className="text-neutral-400 text-sm mb-8">
          Enter your admin password to view and manage applications.
        </p>

        <label className="block space-y-2 mb-6">
          <span className="font-condensed uppercase tracking-[0.2em] text-xs text-neutral-300">
            Password
          </span>
          <input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            autoFocus
            className="w-full bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#eab308] focus:ring-2 focus:ring-[#eab308]/30 outline-none px-4 py-3 text-white placeholder:text-neutral-600 rounded-sm transition-all"
          />
        </label>

        <button
          type="submit"
          data-testid="admin-login-btn"
          disabled={loading}
          className="w-full inline-flex items-center justify-center gap-3 bg-[#eab308] hover:bg-[#facc15] text-black font-bold uppercase font-condensed tracking-widest px-6 py-3 rounded-sm transition-all hover:-translate-y-0.5 disabled:opacity-60"
        >
          {loading ? (
            <>
              <Loader2 size={16} className="animate-spin" /> Signing in
            </>
          ) : (
            <>
              Sign In <ArrowRight size={16} />
            </>
          )}
        </button>
      </form>
    </main>
  );
}
